import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import { Search, Eye, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

interface IncidentReport {
  id: string;
  airport_id: string;
  incident_time: string;
  description: string;
  incident_type: string;
  image_url: string | null;
  reporter_name: string;
  reporter_phone: string;
  reporter_email: string;
  status: string;
  created_at: string;
  cities: {
    name: string;
    code: string;
  };
}

const incidentTypeLabels: Record<string, string> = {
  suspicious: "Suspicious Activity",
  corruption: "Corruption",
  safety: "Safety Issue",
  illegal: "Illegal Activity",
};

export default function AdminIncidentReports() {
  const { profile, loading } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedReport, setSelectedReport] = useState<IncidentReport | null>(null);

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ["incident-reports"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("incident_reports")
        .select(`
          *,
          cities (
            name,
            code
          )
        `)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as IncidentReport[];
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, newStatus }: { id: string; newStatus: string }) => {
      const { error } = await supabase
        .from("incident_reports")
        .update({ status: newStatus })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incident-reports"] });
      toast.success("Status updated successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update status");
    },
  });

  const filteredReports = reports.filter((report) => {
    const matchesSearch = 
      report.reporter_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.cities.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || report.status === statusFilter;
    const matchesType = typeFilter === "all" || report.incident_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const toggleStatus = (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "resolved" ? "unresolved" : "resolved";
    updateStatusMutation.mutate({ id, newStatus });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <>
      <SEOHead
        title="Incident Reports Management | Admin"
        description="Manage security incident reports"
      />
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardHeader style={{ backgroundColor: 'orange' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-8 w-8 text-destructive" />
                  <CardTitle className="text-3xl">Security Incident Reports</CardTitle>
                </div>
                <Badge variant="secondary" className="text-lg px-4 py-2">
                  {filteredReports.length} Reports
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, description, or city..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="unresolved">Unresolved</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="suspicious">Suspicious Activity</SelectItem>
                    <SelectItem value="corruption">Corruption</SelectItem>
                    <SelectItem value="safety">Safety Issue</SelectItem>
                    <SelectItem value="illegal">Illegal Activity</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Table */}
              {isLoading ? (
                <div className="text-center py-8">Loading reports...</div>
              ) : filteredReports.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No incident reports found
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Airport</TableHead>
                        <TableHead>Incident Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Reporter</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReports.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell className="font-medium">
                            {report.cities.name}
                          </TableCell>
                          <TableCell>
                            {format(new Date(report.incident_time), "PPp")}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {incidentTypeLabels[report.incident_type]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">{report.reporter_name}</div>
                              <div className="text-sm text-muted-foreground">
                                {report.reporter_phone}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleStatus(report.id, report.status)}
                              className="gap-2"
                            >
                              {report.status === "resolved" ? (
                                <>
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                  <span className="text-green-600">Resolved</span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 text-red-600" />
                                  <span className="text-red-600">Unresolved</span>
                                </>
                              )}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedReport(report)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Details Modal */}
      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Incident Report Details</DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-muted-foreground">Airport</label>
                  <p className="text-base mt-1">{selectedReport.cities.name} ({selectedReport.cities.code})</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-muted-foreground">Incident Time</label>
                  <p className="text-base mt-1">{format(new Date(selectedReport.incident_time), "PPPp")}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-muted-foreground">Type</label>
                  <p className="text-base mt-1">
                    <Badge>{incidentTypeLabels[selectedReport.incident_type]}</Badge>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-muted-foreground">Status</label>
                  <p className="text-base mt-1">
                    <Badge variant={selectedReport.status === "resolved" ? "default" : "destructive"}>
                      {selectedReport.status === "resolved" ? "Resolved" : "Unresolved"}
                    </Badge>
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-muted-foreground">Description</label>
                <p className="text-base mt-1 whitespace-pre-wrap">{selectedReport.description}</p>
              </div>

              {selectedReport.image_url && (
                <div>
                  <label className="text-sm font-semibold text-muted-foreground">Evidence Image</label>
                  <img 
                    src={selectedReport.image_url} 
                    alt="Incident evidence" 
                    className="mt-2 rounded-lg border max-w-full h-auto"
                  />
                </div>
              )}

              <div className="border-t pt-4">
                <label className="text-sm font-semibold text-muted-foreground">Reporter Information</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedReport.reporter_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{selectedReport.reporter_phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedReport.reporter_email}</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  Reported on {format(new Date(selectedReport.created_at), "PPPp")}
                </p>
              </div>

              <Button
                onClick={() => toggleStatus(selectedReport.id, selectedReport.status)}
                className="w-full"
              >
                Mark as {selectedReport.status === "resolved" ? "Unresolved" : "Resolved"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
